'use client';

import type { ProductInformation } from '@dealerportal/common';
import { useTranslations } from '@dealerportal/i18n/client';
import { useClientContext } from '@dealerportal/react-helpers';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import classNames from 'classnames';
import {
  forwardRef,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
// Not via the library barrel: that creates a module cycle with the barrel,
// which React Fast Refresh turns into a "before initialization" error.
import { useShopifyScript } from '../../useShopifyScript';

export interface SamplesHandle {
  triggerClick: (upid: string) => void;
}

export const Samples = forwardRef<
  SamplesHandle,
  {
    productInfo: Partial<ProductInformation>;
    samplePrice?: number;
    icon?: string;
    replaceClass?: object;
    visible?: boolean;
    labelClass?: string;
    isLoading?: boolean;
    onClick?: MouseEventHandler<HTMLButtonElement>;
  }
>(
  (
    {
      productInfo,
      samplePrice,
      icon,
      replaceClass = {},
      visible = true,
      isLoading = false,
      onClick = () => {},
      labelClass = '',
    },
    ref
  ) => {
    const ctx = useClientContext();
    const t = useTranslations('products');
    const [isScriptReady, setIsScriptReady] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const pendingUpidRef = useRef<string | null>(null);

    const checkScriptReady = useCallback(
      (interval: string | number | NodeJS.Timeout | undefined) => {
        if ((window as any)['ShopifyBuy'] !== undefined) {
          setIsScriptReady(true);
          clearInterval(interval);
        }
      },
      []
    );

    useEffect(() => {
      const intId = setInterval(checkScriptReady, 1000);
      checkScriptReady(intId);
      return () => {
        clearInterval(intId);
      };
    }, [checkScriptReady]);

    const { loadScript, isLoaded } = useShopifyScript({ autoRegister: true });

    useEffect(() => {
      if (!isLoaded) loadScript();
    }, [isLoaded, loadScript]);

    // Once Shopify finishes bootstrapping, fire any click that arrived early.
    useEffect(() => {
      if (isLoaded && pendingUpidRef.current !== null) {
        const upid = pendingUpidRef.current;
        pendingUpidRef.current = null;
        const button =
          containerRef.current?.querySelector<HTMLButtonElement>('button');
        if (button) {
          button.setAttribute('data-product-handle', upid);
          button.click();
        }
      }
    }, [isLoaded]);

    useImperativeHandle(
      ref,
      () => ({
        triggerClick(upid: string) {
          const button =
            containerRef.current?.querySelector<HTMLButtonElement>('button');
          if (isLoaded && button) {
            button.setAttribute('data-product-handle', upid);
            button.click();
          } else {
            // Queue the click; the effect above fires it once isLoaded flips true.
            pendingUpidRef.current = upid;
          }
        },
      }),
      [isLoaded]
    );

    return (
      <div ref={containerRef}>
        <BetterButton
          icon={icon}
          label={`${samplePrice ? `($${samplePrice}) ` : ''}${t('orderSample')}`}
          labelClassName={classNames('ui-text-sm', labelClass)}
          btnStyle="primary"
          attributes={{
            id: 'adhawk-shopify-camilyo-marketplace-order-sample',
            onClick: onClick,
            'data-product-handle': productInfo.UPID,
            'data-universal-retailer-id': ctx.universalRetailerId,
            disabled: !isScriptReady,
          }}
          className={classNames({
            '!ui-hidden': !visible,
          })}
          replaceClass={replaceClass}
          isLoading={isLoading || !isScriptReady}
        />
      </div>
    );
  }
);
Samples.displayName = 'Samples';
